import { captureException } from '@sentry/nuxt';
import { GetFinTokenRequest } from 'gportal-grpc/gportal/api/support/v1/requests_pb';

let booted = false;

/**
 * Whether the messenger has been booted AND its launcher should currently be
 * on screen. Tracked here rather than in the caller because `booted` lives
 * here: showing the launcher is only meaningful once a boot has happened.
 */
let launcherVisible = false;

/**
 * The visibility most recently ASKED for, which is not the same as the
 * visibility currently applied. `setIntercomLauncherVisible` awaits a dynamic
 * import, and a support-to-support navigation issues two calls across that
 * gap — the old page hiding on unmount, the new page showing on mount. Whoever
 * resolves second must apply the latest intent rather than its own stale
 * argument, or the launcher ends up hidden on a page that just asked for it.
 */
let desiredLauncherVisible = false;

/**
 * Shows or hides the messenger launcher, for callers that want the chat on
 * some routes and not others.
 *
 * A no-op before the first boot — there is nothing to hide — so a page that
 * calls this without booting simply does nothing.
 *
 * Deliberately NOT `shutdown()`: that ends the messenger session and clears
 * unread state, so a customer who navigates away mid-conversation would come
 * back to an empty one, and every return trip would re-mint a Fin token.
 * Hiding the launcher keeps the conversation exactly where they left it.
 */
export async function setIntercomLauncherVisible(
  visible: boolean,
): Promise<void> {
  if (import.meta.server) {
    return;
  }

  // Recorded BEFORE the `booted` guard, not after. A customer can leave the support page while
  // `bootIntercom` is still fetching its token: the hide arrives with nothing booted yet, and if it
  // returned here the boot would go on to show a launcher on whatever page they landed on, with
  // nothing left to take it down. Recording the intent lets the boot itself honour it.
  desiredLauncherVisible = visible;

  if (!booted || launcherVisible === desiredLauncherVisible) {
    return;
  }

  try {
    const { update } = await import('@intercom/messenger-js-sdk');

    // Re-read after the await rather than trusting `visible`: a later call may have changed the
    // intent, or already applied it, while this one waited on the import.
    if (launcherVisible === desiredLauncherVisible) {
      return;
    }
    const target = desiredLauncherVisible;

    update({ hide_default_launcher: !target }); // eslint-disable-line camelcase
    launcherVisible = target;
  } catch (err) {
    captureException(err, {
      tags: { feature: 'intercom', op: 'setLauncherVisible' },
    });
  }
}

/**
 * Boots the Intercom messenger with Fin (AI support bot) authentication.
 *
 * Fetches a signed JWT (GetFinToken over gRPC-web) and passes it to Intercom as
 * `intercom_user_jwt`. Boots at most once per page load. Never throws or
 * surfaces anything to the user: any failure (dynamic import, token fetch, SDK
 * init) is swallowed and logged.
 *
 * Caller is responsible for gating on the `support-chat` feature flag, and for
 * deciding which routes the messenger belongs on — see `useSupportChat`.
 */
export async function bootIntercom(): Promise<void> {
  if (import.meta.server || booted) {
    return;
  }

  const authStore = useAuthStore();
  const user = authStore.user;
  if (!authStore.isAuthenticated || !user) {
    return;
  }

  try {
    const grpcSupport = useGrpcSupport();
    const meta = authStore.getAuthorizationMetadata();

    const [{ default: Intercom }, { response, error }] = await Promise.all([
      import('@intercom/messenger-js-sdk'),
      grpcSupport.executeApiCallActive(
        'getFinToken',
        new GetFinTokenRequest(),
        meta,
      ),
    ]);

    if (error || !response?.token) {
      if (error) {
        captureException(error, {
          tags: { feature: 'intercom', op: 'fetchFinToken' },
        });
      }

      return;
    }

    // Guard again: the awaits above yield, and a parallel call could have
    // booted. Claim the slot before the (synchronous) Intercom() call so a
    // concurrent invocation can't double-boot.
    if (booted) {
      return;
    }
    booted = true;
    // Boot with whatever visibility was last asked for, rather than assuming the caller still wants
    // it. Everything above this line is asynchronous, so a customer can have left the support page
    // while the token was in flight — in which case the intent is already `false` and the launcher
    // must never appear.
    launcherVisible = desiredLauncherVisible;

    Intercom({
      app_id: 'b51zfxgs', // eslint-disable-line camelcase
      region: 'eu',
      hide_default_launcher: !launcherVisible, // eslint-disable-line camelcase
      user_id: user.id, // eslint-disable-line camelcase
      name: `${user.firstName} ${user.lastName}`.trim(),
      email: user.email,
      intercom_user_jwt: response.token, // eslint-disable-line camelcase
    });
  } catch (err) {
    captureException(err, {
      tags: { feature: 'intercom', op: 'bootIntercom' },
    });
  }
}
